import { useState } from 'react';
import { Link } from 'react-router';
import { api, type CallingState, type SystemInfo } from '../../api/client';
import { Button, Card, Chip, Kpi, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { dateOnly, shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';
// The call records service (apps/records).
import { RecordsServiceCard } from './RecordsCard';
import { RecordingsStorageCard } from './RecordingsStorageCard';
import { CarrierCard } from './CarrierCard';
import { GoogleCalendarCard } from './GoogleCalendarCard';
import { ArchiveCard } from './ArchiveCard';
import { ArchiveHealthCard } from './ArchiveHealthCard';
import { RestoreDrillCard } from './RestoreDrillCard';
import { RatingCard } from './RatingCard';
import { TeampaceReconCard, TeampaceSystemCard } from './TeampaceCard';
import { GatewayCard } from './GatewayCard';
import { AiSettingsCard, BlockStorageCard, LiveKitCard, MailSettingsCard, TranscriptsSettingsCard } from './SettingsGroupCard';
import { DeployDayCard } from './DeployDayCard';
import { CallsAbroadSystemCard } from './CallsAbroadSystemCard';
import { RetentionCard } from './RetentionCard';
import { VirusCheckCard } from './VirusCheckCard';
// Incoming webhooks into channels (1.12.19): their base address and container.
import { WebhooksCard } from './WebhooksCard';
// Smart messaging: its own container's status.
import { SmartServiceCard } from '../../components/messages/SmartServiceCard';
// Push notifications (the installable Stitch app).
import { PushCard } from './PushCard';
import { PatchesCard, PatchRingsCard } from '../../components/PatchStatus';

const PROVIDER = { resend: 'Resend', smtp: 'SMTP server', outbox: 'Kept here (testing)' } as const;

/** How invitations and password resets are sent, and a test email to prove it. */
function EmailCard({ mail }: { mail: SystemInfo['mail'] }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  async function test() {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.testEmail();
      setResult({ tone: 'ok', text: `Sent to ${r.to}. It should arrive within a minute.` });
    } catch (e) {
      setResult({ tone: 'crit', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="Email">
      <div className="card-body">
        <dl className="dl">
          <dt>Sent through</dt>
          <dd>{mail ? <Chip tone="ok">{PROVIDER[mail.provider]}</Chip> : <Chip tone="warn">Not set up</Chip>}</dd>
          {mail?.from && (
            <>
              <dt>From</dt>
              <dd className="mono" style={{ overflowWrap: 'anywhere' }}>
                {mail.from}
              </dd>
            </>
          )}
          <dt>Invitation links</dt>
          <dd>{mail ? 'Emailed, seen only by the person invited' : 'Shown to whoever sends the invitation, to pass on'}</dd>
        </dl>
        {result && (
          <div style={{ marginTop: 12 }}>
            <Notice tone={result.tone}>{result.text}</Notice>
          </div>
        )}
      </div>
      <div className="card-foot">
        {mail ? (
          <Button icon="send" onClick={() => void test()} disabled={busy}>
            {busy ? 'Sending…' : 'Send me a test email'}
          </Button>
        ) : (
          <span className="muted" style={{ fontSize: 13 }}>
            Set it up in Email settings below (or RESEND_API_KEY or SMTP_URL, and MAIL_FROM, in EasyPanel).
          </span>
        )}
      </div>
    </Card>
  );
}

const CALLING_LABEL: Record<CallingState, { label: string; tone: 'ok' | 'warn' | 'crit' | 'neutral' }> = {
  registered: { label: 'Registered', tone: 'ok' },
  registering: { label: 'Registering', tone: 'neutral' },
  off: { label: 'Off', tone: 'neutral' },
  failing: { label: 'Not answering', tone: 'warn' },
  error: { label: 'Couldn’t hand over', tone: 'warn' },
  'no-extension': { label: 'No such extension', tone: 'warn' },
  auth_failing: { label: 'Login refused', tone: 'crit' },
  'no-password': { label: 'No SIP password', tone: 'crit' }
};

/** App calling: the gateway that registers signed-in people's extensions, and how that's going. */
function CallingCard() {
  const calling = useData(() => api.calling(), []);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const c = calling.data;
  async function check() {
    setBusy(true);
    setProblem(null);
    try {
      await api.checkCalling();
      calling.reload();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  if (calling.error) {
    return (
      <Card title="App calling">
        <LoadError error={calling.error} retry={calling.reload} />
      </Card>
    );
  }
  if (!c) {
    return (
      <Card title="App calling">
        <Loading />
      </Card>
    );
  }
  if (!c.configured) {
    return (
      <Card title="App calling">
        <div className="card-body">
          <dl className="dl">
            <dt>Status</dt>
            <dd>
              <Chip>Off</Chip>
            </dd>
          </dl>
        </div>
        <div className="card-foot">
          <span className="muted" style={{ fontSize: 13 }}>
            Set GATEWAY_URL and GATEWAY_TOKEN on the server, then redeploy. Then turn App calling on per customer in Updates: people there are registered with the phone system when they sign in. See docs/app-calling.md.
          </span>
        </div>
      </Card>
    );
  }
  const issues = c.people.filter((p) => p.state !== 'registered' && p.state !== 'registering');
  const shown = (Object.keys(CALLING_LABEL) as CallingState[]).filter((k) => c.counts[k] > 0);
  return (
    <Card title="App calling">
      <div className="card-body">
        <dl className="dl">
          <dt>Gateway</dt>
          <dd>
            {c.lastSync === null ? <Chip>Not asked yet</Chip> : c.lastSync.ok ? <Chip tone="ok">Answering</Chip> : <Chip tone="crit">Not answering</Chip>}
            <div className="mono" style={{ overflowWrap: 'anywhere', marginTop: 4 }}>
              {c.gateway}
            </div>
            {c.lastSync && !c.lastSync.ok && c.lastSync.error && <small className="muted">{c.lastSync.error}</small>}
          </dd>
          <dt>Gateway version</dt>
          <dd>
            {c.version && typeof c.version === 'object' ? (
              <>
                <span className="mono">{c.version.version || 'unknown'}</span>
                {c.version.startedAt && <small className="muted" style={{ display: 'block' }}>Running since {shortWhen(c.version.startedAt)}</small>}
              </>
            ) : c.version === 'older' ? (
              <>
                <Chip tone="warn">Older than 1.12.12</Chip>
                <small className="muted" style={{ display: 'block' }}>
                  Update it: copy the newest stitch-gateway-install-&lt;version&gt;.sh to the gateway server and run it with sudo. Conference calls need 1.12.7 or later.
                </small>
              </>
            ) : (
              <span className="muted">Not known</span>
            )}
          </dd>
          <dt>Registers people at</dt>
          <dd>
            {c.companies === null ? (
              'Every tenant'
            ) : (
              <Link to="/admin/updates/app-calling">{c.companies === 1 ? '1 company' : `${c.companies} companies`} with App calling on</Link>
            )}
          </dd>
          <dt>Phone system address</dt>
          <dd className="mono">
            {c.registrar} ({c.transport})
          </dd>
          <dt>SIP passwords</dt>
          <dd>
            {c.passwords === 'stitch' ? (
              <>
                Read from the phone system when it shows them; otherwise Stitch sets its own on the extension
                <small className="muted" style={{ display: 'block' }}>
                  Long and random, sealed with DATA_ENCRYPTION_KEY, never shown. Devices set up by hand with the old SIP password stop registering; provisioned phones aren’t affected.
                </small>
              </>
            ) : (
              <>
                Read from the phone system only
                <small className="muted" style={{ display: 'block' }}>
                  It must show them (rest_show_passwords). Stitch sets its own with DATA_ENCRYPTION_KEY set and GATEWAY_SET_SIP_PASSWORDS not false.
                </small>
              </>
            )}
          </dd>
          {c.lastSync && (
            <>
              <dt>Last checked</dt>
              <dd>{shortWhen(c.lastSync.at)}</dd>
            </>
          )}
          {c.calls && (
            <>
              <dt>Calls in the app</dt>
              <dd>
                {c.calls.stream === 'connected' ? (
                  <Chip tone="ok">Hearing the gateway’s calls</Chip>
                ) : c.calls.stream === 'portal' ? (
                  // Stitch Admin in its own container: the portal's container hears them (once).
                  <Chip>Heard by the portal’s container</Chip>
                ) : c.calls.stream === 'connecting' ? (
                  <Chip tone="warn">Connecting to the gateway…</Chip>
                ) : (
                  <Chip>Off</Chip>
                )}
                {c.calls.stream !== 'connected' && c.calls.error && <small className="muted" style={{ display: 'block' }}>{c.calls.error}</small>}
                {c.calls.calls > 0 && <small className="muted" style={{ display: 'block' }}>{c.calls.calls === 1 ? '1 call now' : `${c.calls.calls} calls now`}</small>}
              </dd>
              <dt>Call sound</dt>
              <dd>
                {!c.calls.livekit ? (
                  <Chip tone="crit">Stitch has no LiveKit (LIVEKIT_*)</Chip>
                ) : c.calls.stream === 'portal' ? (
                  <Chip>Stitch has LiveKit; the gateway’s side shows in the portal’s container</Chip>
                ) : c.calls.media === 'none' ? (
                  <Chip tone="crit">The gateway has no LiveKit: calls would be silent</Chip>
                ) : c.calls.media === 'livekit' ? (
                  <Chip tone="ok">Through LiveKit</Chip>
                ) : (
                  <Chip>Not known until the gateway answers</Chip>
                )}
              </dd>
            </>
          )}
          <dt>Extensions</dt>
          <dd style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {shown.length === 0 ? (
              <span className="muted">Nobody signed in to register</span>
            ) : (
              shown.map((k) => (
                <Chip key={k} tone={CALLING_LABEL[k].tone} small>
                  {CALLING_LABEL[k].label}: {c.counts[k]}
                </Chip>
              ))
            )}
          </dd>
        </dl>
        {issues.length > 0 && (
          <ul className="plain-list" style={{ marginTop: 12 }}>
            {issues.slice(0, 20).map((p) => (
              <li key={`${p.domain}|${p.extension}`}>
                <span className="cell-stack">
                  <span>
                    <strong style={{ fontWeight: 650 }}>{p.extension}</strong> <span className="mono muted">{p.domain}</span> <Chip tone={CALLING_LABEL[p.state].tone} small>{CALLING_LABEL[p.state].label}</Chip>
                  </span>
                  <small className="muted">
                    {p.names.join(', ')}
                    {p.detail ? ` · ${p.code ? `${p.code} ` : ''}${p.detail}` : ''}
                  </small>
                </span>
              </li>
            ))}
          </ul>
        )}
        {problem && (
          <div style={{ marginTop: 12 }}>
            <Notice tone="crit">{problem}</Notice>
          </div>
        )}
      </div>
      <div className="card-foot">
        <Button icon="refresh" onClick={() => void check()} disabled={busy}>
          {busy ? 'Checking…' : 'Check now'}
        </Button>
      </div>
    </Card>
  );
}

const SIDE: Record<NonNullable<SystemInfo['surface']>['name'], { title: string; detail: string }> = {
  all: { title: 'Stitch Admin and the portal', detail: 'One address for both (STITCH_SURFACE=all).' },
  admin: { title: 'Stitch Admin', detail: 'Its own container, for the Stitch team only (STITCH_SURFACE=admin).' },
  portal: { title: 'The portal', detail: 'Companies only; Stitch Admin is in its own container (STITCH_SURFACE=portal).' },
  webhooks: { title: 'Webhooks', detail: 'Incoming webhooks only (STITCH_SURFACE=webhooks).' }
};

/** Which side of Stitch this container serves, the other side's address, and what runs where. */
function ContainerCard({ surface }: { surface: NonNullable<SystemInfo['surface']> }) {
  const side = SIDE[surface.name];
  const split = surface.name !== 'all';
  const elsewhere = surface.jobs.filter((j) => !j.here);
  return (
    <Card title="This container">
      <div className="card-body">
        <Chip tone={split ? 'brand' : 'neutral'}>{side.title}</Chip>
        <p className="muted" style={{ margin: '6px 0 12px', fontSize: 13 }}>
          {side.detail}
        </p>
        {(surface.portalUrl || surface.adminUrl) && (
          <dl className="dl">
            {surface.portalUrl && (
              <>
                <dt>The portal</dt>
                <dd className="mono" style={{ overflowWrap: 'anywhere' }}>
                  {split ? (
                    <a href={surface.portalUrl} target="_blank" rel="noopener">
                      {surface.portalUrl}
                    </a>
                  ) : (
                    surface.portalUrl
                  )}
                </dd>
              </>
            )}
            {surface.adminUrl && (
              <>
                <dt>Stitch Admin</dt>
                <dd className="mono" style={{ overflowWrap: 'anywhere' }}>
                  {surface.adminUrl}
                </dd>
              </>
            )}
          </dl>
        )}
        {split && elsewhere.length > 0 && (
          <>
            <p className="field-label" style={{ margin: '14px 0 4px' }}>
              In the portal’s container only
            </p>
            <ul className="plain-list">
              {elsewhere.map((j) => (
                <li key={j.name}>
                  <span className="cell-stack">
                    <span>{j.name}</span>
                    <small className="muted">{j.how}</small>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      {split && (
        <div className="card-foot">
          <span className="muted" style={{ fontSize: 13 }}>
            Both containers share the database and Redis; everything else runs in both, once (database locks, leases in Redis). See docs/scaling.md.
          </span>
        </div>
      )}
    </Card>
  );
}

export function AdminSystem() {
  const sys = useData(() => api.system(), []);
  const s = sys.data;

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="System"
        actions={
          <Button icon="refresh" onClick={sys.reload}>
            Refresh
          </Button>
        }
      />
      <div className="content">
        {sys.error ? (
          <div className="card">
            <LoadError error={sys.error} retry={sys.reload} />
          </div>
        ) : !s ? (
          <Loading />
        ) : (
          <>
            <section className="kpis" aria-label="System status">
              <Kpi
                label="Phone system"
                small
                value={s.pbx.reachable ? 'Reachable' : 'Unreachable'}
                foot={s.pbx.reachable ? `Version ${s.pbx.version || 'unknown'}` : `Error: ${s.pbx.code ?? 'unknown'}`}
              />
              <Kpi
                label="Version check"
                small
                value={s.pbx.supported ? 'Supported (69)' : 'Not confirmed'}
                foot={s.pbx.supported ? 'Writes follow the settings below' : 'The API runs read-only until it is'}
              />
              <Kpi
                label="Stitch data"
                small
                value={s.store === 'postgres' ? 'Postgres' : s.store === 'file' ? 'On this server' : 'In memory'}
                foot={s.store === 'memory' ? 'Cleared on restart' : 'Kept across restarts'}
              />
              <Kpi label="Running since" small value={dateOnly(s.startedAt)} foot={`${shortWhen(s.startedAt)} · ${s.version} (${s.build})`} />
            </section>

            {s.notices.length > 0 && (
              <div id="notices" className="sys-anchor sys-notices">
                {s.notices.map((n) => (
                  <Notice key={n} tone="warn">
                    {n}
                  </Notice>
                ))}
              </div>
            )}

            <div className="cols">
              <div className="col-main">
                {/* What a deploy still needs (1.13.0, 3122); its lines link to the cards below by anchor. */}
                <DeployDayCard />
                <div id="carrier" className="sys-anchor">
                  <CarrierCard />
                </div>
                <div id="google" className="sys-anchor">
                  <GoogleCalendarCard />
                </div>
                <div id="archive-health" className="sys-anchor">
                  <ArchiveHealthCard />
                </div>
                <div id="archive" className="sys-anchor">
                  <ArchiveCard />
                </div>
                <div id="restore-drill" className="sys-anchor">
                  <RestoreDrillCard />
                </div>
                <div id="retention" className="sys-anchor">
                  <RetentionCard />
                </div>
                <div id="rating" className="sys-anchor">
                  <RatingCard />
                </div>
                <div id="teampace" className="sys-anchor">
                  <TeampaceSystemCard />
                </div>
                <div id="reconciliation" className="sys-anchor">
                  <TeampaceReconCard />
                </div>
                <div id="calls-abroad" className="sys-anchor">
                  <CallsAbroadSystemCard />
                </div>
                {/* The call gateway's address and token, kept in Stitch (1.13.0, 2845). */}
                <div id="gateway" className="sys-anchor">
                  <GatewayCard />
                </div>
                <div id="livekit" className="sys-anchor">
                  <LiveKitCard />
                </div>
                <div id="block-storage" className="sys-anchor">
                  <BlockStorageCard />
                </div>
                <div id="ai" className="sys-anchor">
                  <AiSettingsCard />
                </div>
                <div id="transcripts" className="sys-anchor">
                  <TranscriptsSettingsCard />
                </div>
                <VirusCheckCard />
                <WebhooksCard />
                <Card title="Features on the 69 build">
                  <div className="card-body" style={{ paddingBottom: 0 }}>
                    <span className="muted">
                      Every feature can be changed through Stitch, on every customer’s tenant (and test tenants named in PBX_WRITE_TENANTS). “Verified” means the admin GUI’s own calls were recorded on your build; the rest come from the reference and its code. Either way, each change is read back as soon as it’s saved, and undone if the phone system didn’t keep it. See docs/verify-69.md.
                    </span>
                  </div>
                  <div className="table-wrap" style={{ marginTop: 12 }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">Feature</th>
                          <th scope="col">Status</th>
                          <th scope="col" className="hide-sm">
                            Settings not recorded yet
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(s.accountTypes ?? []).map((t) => (
                          <tr key={t.kind}>
                            <td>
                              <span className="cell-stack">
                                <strong style={{ fontWeight: 650 }}>{t.title}</strong>
                                <small className="mono">{t.vodiaType}</small>
                              </span>
                            </td>
                            <td>
                              {t.verified ? (
                                <Chip tone="ok">Verified{t.verifiedOn ? ` on ${t.verifiedOn}` : ''}</Chip>
                              ) : t.trial ? (
                                <Chip tone="warn">On trial (test tenant)</Chip>
                              ) : t.writable ? (
                                <Chip tone="brand">Open, read back</Chip>
                              ) : (
                                <Chip tone="warn">View only</Chip>
                              )}
                            </td>
                            <td className="hide-sm">{t.verified || t.provisional.length === 0 ? <span className="muted">–</span> : <small>{t.provisional.join(', ')}</small>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
                <Card title="PBX endpoints on the 69 build">
                  <div className="card-body" style={{ paddingBottom: 0 }}>
                    <span className="muted">
                      Each endpoint is checked against your production 69 build before the portal relies on it. See docs/verify-69.md in the code.
                    </span>
                  </div>
                  <div className="table-wrap" style={{ marginTop: 12 }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">Endpoint</th>
                          <th scope="col">Request</th>
                          <th scope="col">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.endpoints.map((e) => (
                          <tr key={e.name}>
                            <td>
                              <span className="cell-stack">
                                <strong style={{ fontWeight: 650 }}>{e.name}</strong>
                                <small>{e.notes}</small>
                              </span>
                            </td>
                            <td>
                              <span className="cell-stack">
                                <span className="mono">{e.method}</span>
                                <small className="mono">
                                  {e.template.split('/').map((seg, i) => (
                                    <span key={i}>
                                      {i > 0 && '/'}
                                      {i > 0 && <wbr />}
                                      {seg}
                                    </span>
                                  ))}
                                </small>
                              </span>
                            </td>
                            <td>{e.verified ? <Chip tone="ok">Verified{e.verifiedOn ? ` on ${e.verifiedOn}` : ''}</Chip> : <Chip tone="warn">Not verified</Chip>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
              <div className="col-side">
                <Card title="Write fence" id="write-fence">
                  <div className="card-body">
                    <dl className="dl">
                      <dt>Mode</dt>
                      <dd>{s.writes.readOnly ? <Chip tone="warn">Read-only</Chip> : <Chip tone="ok">Writes allowed</Chip>}</dd>
                      <dt>Customers’ tenants</dt>
                      <dd>
                        {s.writes.readOnly ? (
                          <span className="muted">None while read-only</span>
                        ) : s.writes.linked === null || s.writes.linked === undefined ? (
                          <span className="muted">Only the ones named below</span>
                        ) : (
                          `Every customer’s (${s.writes.linked})`
                        )}
                      </dd>
                      <dt>Test tenants</dt>
                      <dd>
                        {s.writes.tenants.length === 0 ? (
                          <span className="muted">None named</span>
                        ) : s.writes.tenants.includes('*') ? (
                          <Chip tone="warn">Every tenant (*)</Chip>
                        ) : (
                          s.writes.tenants.map((t) => (
                            <div key={t} className="mono">
                              {t}
                            </div>
                          ))
                        )}
                      </dd>
                      <dt>System settings</dt>
                      <dd>{s.writes.systemWrites ? <Chip tone="crit">Can change</Chip> : <Chip>Locked</Chip>}</dd>
                    </dl>
                  </div>
                  <div className="card-foot">
                    <span className="muted" style={{ fontSize: 13 }}>
                      Stitch changes every tenant linked to a customer. PBX_WRITE_TENANTS only names test tenants no customer is linked to; PBX_READ_ONLY and PBX_ALLOW_SYSTEM_WRITES are set on the server. Every other tenant is read-only, whatever the portal asks.
                    </span>
                  </div>
                </Card>
                {s.surface && <ContainerCard surface={s.surface} />}
                <EmailCard mail={s.mail} />
                <div id="mail" className="sys-anchor">
                  <MailSettingsCard />
                </div>
                <CallingCard />
                <RecordsServiceCard />
                {/* The phone system's cloud-stored recordings, read with Stitch's own key (1.12.27). */}
                <div id="recordings-storage" className="sys-anchor">
                  <RecordingsStorageCard />
                </div>
                {/* Smart messaging: running, last heartbeat, event backlog, timers. */}
                <SmartServiceCard />
                <PushCard />
                {/* Patches: off, or whether LiveKit answers and its webhooks arrive. */}
                <div id="patches" className="sys-anchor">
                  <PatchesCard />
                </div>
                <PatchRingsCard />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
